import './TrackmanParameters.scss';
import { useQuery } from '@tanstack/react-query';
import { useServices } from 'index';
import { useTranslation } from 'react-i18next';
import { primaryCategoriesOrder, subCategoriesOrder } from './categoriesOrder';
import { Tab, Tabs } from '@trackman/web-shared-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TrackmanParametersTabContent } from './TrackmanParametersTabContent/TrackmanParametersTabContent';
import { TrackmanParametersTabCategories } from './TrackmanParametersTabCategories/TrackmanParametersTabCategories';
import { ParameterModel } from 'types/trackmanParameters';
import { useSearchParams } from 'react-router-dom';
import useSetPageTitle from 'utilities/useSetPageTitle';
import Box from '@mui/material/Box/Box';

export const TrackmanParameters = () => {
  const { t } = useTranslation();

  useSetPageTitle({
    title: t('TrackmanParameters', { ns: 'trackmanParameters' }),
  });

  const trackmanParametersService = useServices().trackmanParametersService;
  const trackmanParametersList = useQuery({
    queryKey: ['trackmanParametersList'],
    queryFn: trackmanParametersService.getTrackmanParameters,
    refetchOnWindowFocus: false,
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
  const [selectedSubjectIndex, setSelectedSubjectIndex] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const sortedTabsList = useMemo(() => {
    if (!trackmanParametersList.data) {
      return undefined;
    }

    const sortedEntries: [string, [string, ParameterModel[]][]][] = Object.entries(trackmanParametersList.data)
      ?.sort((a, b) => primaryCategoriesOrder.indexOf(a[0]) - primaryCategoriesOrder.indexOf(b[0]))
      .map(([categoryName, subcategories]) => {
        const sortedSubcategories = Object.entries(subcategories).sort(
          (a, b) => subCategoriesOrder[categoryName].indexOf(a[0]) - subCategoriesOrder[categoryName].indexOf(b[0])
        );
        return [categoryName, sortedSubcategories];
      });

    return sortedEntries;
  }, [trackmanParametersList.data]);

  useEffect(() => {
    if (sortedTabsList && sortedTabsList.length > 0) {
      setSearchParams({
        primaryCategoryKey: sortedTabsList[selectedTabIndex][0],
        categoryKey: sortedTabsList[selectedTabIndex][1][selectedCategoryIndex][0],
        parameterKey: sortedTabsList[selectedTabIndex][1][selectedCategoryIndex][1][selectedSubjectIndex].title.replace(/\s/g, ''),
      });
    }
  }, [sortedTabsList]);

  const handleSelectedTabIndexChange = useCallback(
    (event: React.SyntheticEvent, newIndex: number) => {
      setSelectedTabIndex(newIndex);
      setSelectedCategoryIndex(0);
      setSelectedSubjectIndex(0);
      setSearchParams({
        primaryCategoryKey: sortedTabsList![newIndex][0],
        categoryKey: sortedTabsList![newIndex][1][0][0],
        parameterKey: sortedTabsList![newIndex][1][0][1][0].title.replace(/\s/g, ''),
      });
    },
    [sortedTabsList]
  );

  const handleSelectedCategoryIndexChange = useCallback(
    (newIndex: number) => {
      setSelectedCategoryIndex(newIndex);
      setSelectedSubjectIndex(0);
      setSearchParams({
        primaryCategoryKey: sortedTabsList![selectedTabIndex][0],
        categoryKey: sortedTabsList![selectedTabIndex][1][newIndex][0],
        parameterKey: sortedTabsList![selectedTabIndex][1][newIndex][1][0].title.replace(/\s/g, ''),
      });
    },
    [selectedTabIndex, sortedTabsList]
  );

  const handleSelectedSubjectIndexChange = useCallback(
    (newIndex: number) => {
      setSelectedSubjectIndex(newIndex);
      setSearchParams({
        primaryCategoryKey: sortedTabsList![selectedTabIndex][0],
        categoryKey: sortedTabsList![selectedTabIndex][1][selectedCategoryIndex][0],
        parameterKey: sortedTabsList![selectedTabIndex][1][selectedCategoryIndex][1][newIndex].title.replace(/\s/g, ''),
      });
    },
    [selectedTabIndex, selectedCategoryIndex, sortedTabsList]
  );

  const formatStringForTranslation = (input: string): string => {
    return input
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  };

  const getSelectedSubjectData = useMemo((): ParameterModel | undefined => {
    return !sortedTabsList ? undefined : sortedTabsList![selectedTabIndex][1][selectedCategoryIndex][1][selectedSubjectIndex];
  }, [selectedTabIndex, selectedCategoryIndex, selectedSubjectIndex, sortedTabsList]);

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{t('TrackmanParameters', { ns: 'trackmanParameters' })}</h1>
        <span className='page-subtitle'>{t('CheckOutTheDefinitions', { ns: 'learningMaterials' })}</span>
      </div>
      <Box className='tabs-container'>
        <Tabs
          scrollButtons='auto'
          variant='scrollable'
          indicatorColor='primary'
          allowScrollButtonsMobile
          value={selectedTabIndex}
          onChange={handleSelectedTabIndexChange}
        >
          {sortedTabsList?.map(([categoryName], index) => (
            <Tab label={t(formatStringForTranslation(categoryName), { ns: 'trackmanParameters' })} key={`${categoryName}-${index}`}></Tab>
          ))}
        </Tabs>
      </Box>
      {sortedTabsList?.length && (
        <div className='trackman-parameters-wrapper'>
          <TrackmanParametersTabContent selectedSubcategoryData={getSelectedSubjectData} />
          <TrackmanParametersTabCategories
            categoriesList={sortedTabsList![selectedTabIndex][1]}
            selectedCategoryIndex={selectedCategoryIndex}
            selectedSubjectIndex={selectedSubjectIndex}
            selectCategory={(index: number) => handleSelectedCategoryIndexChange(index)}
            selectSubject={(index: number) => handleSelectedSubjectIndexChange(index)}
          />
        </div>
      )}
    </>
  );
};
